import React, { useState } from 'react';
import { PlusIcon, XIcon, TrashIcon, ClockIcon, FileTextIcon, BellOffIcon, BellRingIcon, UserIcon, CalendarIcon } from 'lucide-react';
import { DocumentNotificationOverride, NotificationEvent } from './NotificationsManagement';
import { StatusBadge } from '../../StatusBadge';
interface DocumentOverridesProps {
  documentOverrides: DocumentNotificationOverride[];
  setDocumentOverrides: React.Dispatch<React.SetStateAction<DocumentNotificationOverride[]>>;
  events: NotificationEvent[];
  locale: string;
}
export function DocumentOverrides({
  documentOverrides,
  setDocumentOverrides,
  events,
  locale
}: DocumentOverridesProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState<DocumentNotificationOverride | null>(null);
  const [formData, setFormData] = useState<Partial<DocumentNotificationOverride>>({
    documentId: '',
    documentType: 'invoice',
    documentNumber: '',
    documentName: '',
    overrideType: 'mute',
    affectedEvents: ['all'],
    affectedUsers: ['all'],
    expiresAt: undefined
  });
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const getDocumentTypeLabel = (type: string) => {
    switch (type) {
      case 'invoice':
        return 'Invoice';
      case 'po':
        return 'Purchase Order';
      case 'requisition':
        return 'Requisition';
      case 'vendor':
        return 'Vendor';
      case 'receipt':
        return 'Receipt';
      default:
        return type;
    }
  };
  const handleAddOverride = () => {
    setEditingOverride(null);
    setFormData({
      documentId: '',
      documentType: 'invoice',
      documentNumber: '',
      documentName: '',
      overrideType: 'mute',
      affectedEvents: ['all'],
      affectedUsers: ['all'],
      expiresAt: undefined
    });
    setIsDrawerOpen(true);
  };
  const handleEditOverride = (override: DocumentNotificationOverride) => {
    setEditingOverride(override);
    setFormData(override);
    setIsDrawerOpen(true);
  };
  const handleDeleteOverride = (id: string) => {
    setDocumentOverrides(documentOverrides.filter(override => override.id !== id));
  };
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditingOverride(null);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleSaveOverride = () => {
    if (!formData.documentNumber || !formData.documentName) {
      // Show validation error
      return;
    }
    const newOverride: DocumentNotificationOverride = {
      id: editingOverride?.id || `override-${Date.now()}`,
      documentId: formData.documentId || `doc-${Date.now()}`,
      documentType: formData.documentType as 'invoice' | 'po' | 'requisition' | 'vendor' | 'receipt',
      documentNumber: formData.documentNumber || '',
      documentName: formData.documentName || '',
      overrideType: formData.overrideType as 'mute' | 'force',
      affectedEvents: formData.affectedEvents || ['all'],
      affectedUsers: formData.affectedUsers || ['all'],
      expiresAt: formData.expiresAt,
      createdBy: editingOverride?.createdBy || 'Current User',
      createdAt: editingOverride?.createdAt || new Date().toISOString()
    };
    if (editingOverride) {
      setDocumentOverrides(documentOverrides.map(override => override.id === editingOverride.id ? newOverride : override));
    } else {
      setDocumentOverrides([...documentOverrides, newOverride]);
    }
    setIsDrawerOpen(false);
    setEditingOverride(null);
  };
  return <div data-id="element-19486">
      <div className="mb-6 flex justify-between items-center" data-id="element-19487">
        <div data-id="element-19488">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19489">
            Document-Level Notification Overrides
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-19490">
            Override notification settings for specific documents
          </p>
        </div>
        <button type="button" onClick={handleAddOverride} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19491">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-19492" />
          Add Override
        </button>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-19493">
        <ul className="divide-y divide-gray-200" data-id="element-19494">
          {documentOverrides.length === 0 ? <li className="px-6 py-10 text-center" data-id="element-19495">
              <p className="text-sm text-gray-500" data-id="element-19496">
                No document overrides configured yet.
              </p>
              <button type="button" onClick={handleAddOverride} className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19497">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-19498" />
                Add Your First Override
              </button>
            </li> : documentOverrides.map(override => <li key={override.id} data-id="element-19499">
                <div className="px-4 py-4 sm:px-6" data-id="element-19500">
                  <div className="flex items-center justify-between" data-id="element-19501">
                    <div className="flex items-center" data-id="element-19502">
                      <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-19503" />
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-19504">
                        {override.documentNumber}: {override.documentName}
                      </p>
                    </div>
                    <div className="ml-2 flex-shrink-0 flex" data-id="element-19505">
                      <StatusBadge status={override.overrideType === 'mute' ? 'warning' : 'info'} text={override.overrideType === 'mute' ? 'Muted' : 'Forced'} data-id="element-19506" />
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between" data-id="element-19507">
                    <div className="sm:flex" data-id="element-19508">
                      <p className="flex items-center text-sm text-gray-500" data-id="element-19509">
                        {getDocumentTypeLabel(override.documentType)}
                      </p>
                      {override.expiresAt && <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6" data-id="element-19510">
                          <CalendarIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" aria-hidden="true" data-id="element-19511" />
                          Expires: {formatDate(override.expiresAt)}
                        </p>}
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-19512">
                      <p className="flex items-center" data-id="element-19513">
                        {override.overrideType === 'mute' ? <BellOffIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" data-id="element-19514" /> : <BellRingIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" data-id="element-19515" />}
                        {override.affectedEvents.includes('all') ? 'All Events' : `${override.affectedEvents.length} Event(s)`}
                        {override.affectedUsers.includes('all') ? ' for All Users' : ` for ${override.affectedUsers.length} User(s)`}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 flex justify-end space-x-2" data-id="element-19516">
                    <button type="button" onClick={() => handleEditOverride(override)} className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19517">
                      Edit
                    </button>
                    <button type="button" onClick={() => handleDeleteOverride(override.id)} className="inline-flex items-center px-2.5 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-19518">
                      <TrashIcon className="h-4 w-4 mr-1" data-id="element-19519" />
                      Delete
                    </button>
                  </div>
                </div>
              </li>)}
        </ul>
      </div>
      {/* Override Configuration Drawer */}
      {isDrawerOpen && <div className="fixed inset-0 overflow-hidden z-50" data-id="element-19520">
          <div className="absolute inset-0 overflow-hidden" data-id="element-19521">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseDrawer} data-id="element-19522"></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-19523">
              <div className="relative w-screen max-w-md" data-id="element-19524">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll" data-id="element-19525">
                  <div className="px-4 py-6 sm:px-6" data-id="element-19526">
                    <div className="flex items-start justify-between" data-id="element-19527">
                      <h2 className="text-lg font-medium text-gray-900" data-id="element-19528">
                        {editingOverride ? 'Edit Override' : 'Create Document Override'}
                      </h2>
                      <div className="ml-3 h-7 flex items-center" data-id="element-19529">
                        <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" onClick={handleCloseDrawer} data-id="element-19530">
                          <span className="sr-only" data-id="element-19531">Close panel</span>
                          <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-19532" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 py-6 overflow-y-auto px-4 sm:px-6" data-id="element-19533">
                    <div className="space-y-6" data-id="element-19534">
                      {/* Document Type */}
                      <div data-id="element-19535">
                        <label htmlFor="documentType" className="block text-sm font-medium text-gray-700" data-id="element-19536">
                          Document Type
                        </label>
                        <select id="documentType" name="documentType" value={formData.documentType} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19537">
                          <option value="invoice" data-id="element-19538">Invoice</option>
                          <option value="po" data-id="element-19539">Purchase Order</option>
                          <option value="requisition" data-id="element-19540">Requisition</option>
                          <option value="vendor" data-id="element-19541">Vendor</option>
                          <option value="receipt" data-id="element-19542">Receipt</option>
                        </select>
                      </div>
                      {/* Document Number */}
                      <div data-id="element-19543">
                        <label htmlFor="documentNumber" className="block text-sm font-medium text-gray-700" data-id="element-19544">
                          Document Number
                        </label>
                        <input type="text" name="documentNumber" id="documentNumber" value={formData.documentNumber} onChange={handleChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="e.g., INV-2023-001" data-id="element-19545" />
                      </div>
                      {/* Document Name */}
                      <div data-id="element-19546">
                        <label htmlFor="documentName" className="block text-sm font-medium text-gray-700" data-id="element-19547">
                          Document Name
                        </label>
                        <input type="text" name="documentName" id="documentName" value={formData.documentName} onChange={handleChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="e.g., Office Supplies Invoice" data-id="element-19548" />
                      </div>
                      {/* Override Type */}
                      <div data-id="element-19549">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-19550">
                          Override Type
                        </label>
                        <div className="mt-2 space-y-4" data-id="element-19551">
                          <div className="flex items-center" data-id="element-19552">
                            <input id="override-mute" name="overrideType" type="radio" value="mute" checked={formData.overrideType === 'mute'} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-19553" />
                            <label htmlFor="override-mute" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-19554">
                              Mute Notifications
                              <span className="block text-sm text-gray-500" data-id="element-19555">
                                Prevent notifications from being sent
                              </span>
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-19556">
                            <input id="override-force" name="overrideType" type="radio" value="force" checked={formData.overrideType === 'force'} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-19557" />
                            <label htmlFor="override-force" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-19558">
                              Force Notifications
                              <span className="block text-sm text-gray-500" data-id="element-19559">
                                Always send notifications regardless of user
                                preferences
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                      {/* Affected Events */}
                      <div data-id="element-19560">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-19561">
                          Affected Events
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-19562">
                          <div className="flex items-center" data-id="element-19563">
                            <input id="all-events" name="all-events" type="checkbox" checked={formData.affectedEvents?.includes('all')} onChange={e => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              affectedEvents: ['all']
                            });
                          } else {
                            setFormData({
                              ...formData,
                              affectedEvents: []
                            });
                          }
                        }} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19564" />
                            <label htmlFor="all-events" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-19565">
                              All Events
                            </label>
                          </div>
                        </div>
                      </div>
                      {/* Affected Users */}
                      <div data-id="element-19566">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-19567">
                          Affected Users
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-19568">
                          <div className="flex items-center" data-id="element-19569">
                            <input id="all-users" name="all-users" type="checkbox" checked={formData.affectedUsers?.includes('all')} onChange={e => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              affectedUsers: ['all']
                            });
                          } else {
                            setFormData({
                              ...formData,
                              affectedUsers: []
                            });
                          }
                        }} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19570" />
                            <label htmlFor="all-users" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-19571">
                              All Users
                            </label>
                          </div>
                        </div>
                      </div>
                      {/* Expiration */}
                      <div data-id="element-19572">
                        <label htmlFor="expiresAt" className="block text-sm font-medium text-gray-700" data-id="element-19573">
                          Expiration Date (Optional)
                        </label>
                        <input type="date" name="expiresAt" id="expiresAt" value={formData.expiresAt?.split('T')[0] || ''} onChange={handleChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-19574" />
                        <p className="mt-1 text-sm text-gray-500" data-id="element-19575">
                          Leave empty for no expiration
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 px-4 py-4 flex justify-end border-t border-gray-200" data-id="element-19576">
                    <button type="button" className="mr-3 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={handleCloseDrawer} data-id="element-19577">
                      Cancel
                    </button>
                    <button type="button" className="bg-indigo-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={handleSaveOverride} data-id="element-19578">
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}